/* eslint-disable max-lines */
import _ from 'lodash';
import moment from 'moment';
import styled from 'styled-components';
import Autocomplete from 'components/base/Autocomplete';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup
} from '@material-ui/core';
import QueryString from 'query-string';
import * as LighthouseService from 'common/services/lighthouse.service';
import * as TrackingService from 'common/services/tracking.service';
import { extractFromQueryString } from 'common/utils/utils';
import { getColor } from 'common/constants/_colors';
import { Flex, Padding } from '../base/layout-components';
import Text from '../base/Text';
import MultipleAvatars from '../MultipleAvatars';
import Icon from '../base/Icon';
import Checkbox from '../base/Checkbox';
import {
  SEARCH_DATE_FORMAT,
  DAYS_PARAM,
  SEARCH_TIMEDATE_FORMAT,
  SEARCH_TIMEDATE_FORMAT_DISPLAY
} from 'common/constants/constants';
import history from 'common/RouterHistory';
import { LBButton } from '@linearb/buzz';

const StyledFormControlLabel = styled(FormControlLabel)`
  > span:nth-child(2) {
    color: ${getColor('shuttleGray')};
  }
`;

const FormControlWrapper = styled(Flex)`
  padding: 1rem 0 0 0.5rem;
`;

const FiltersTitle = styled(Text)`
  text-transform: uppercase;
  color: ${getColor('shuttleGray')};
  padding-top: 1rem;
  padding-bottom: 1rem;
`;

const userReadableLabel = name => {
  switch (name) {
    case 'has-no-pr':
      return 'Has No PR';
    default:
      return _.startCase(name);
  }
};

export const renderRadioButtons = ({
  name,
  options,
  value,
  onChange,
  label,
  idx
}) => (
  <FormControlWrapper key={`rd_${idx}`} direction='column'>
    {label.length ? <FiltersTitle>{_.startCase(label)}</FiltersTitle> : null}
    <FormControl component='fieldset'>
      <RadioGroup
        value={value || ''}
        onChange={e => onChange(name, e.target.value)}
      >
        {options.map(({ value, label }, idx) => (
          <StyledFormControlLabel
            key={idx}
            value={value}
            control={<Radio color='primary' />}
            label={label}
            labelPlacement='end'
          />
        ))}
      </RadioGroup>
    </FormControl>
  </FormControlWrapper>
);

export const renderCheckboxes = ({ name, options, onChange, articleId }) => (
  <FormControlWrapper key={`checkbox-${name}`} direction='column'>
    {name || articleId ? (
      <Flex direction='row' justify='space-between'>
        {name ? <FiltersTitle>{_.startCase(name)}</FiltersTitle> : null}
        {articleId ? (
          <Flex items='center'>
            <LBButton
              onClick={() => LighthouseService.navigateArticle(articleId)}
              size='icon'
              variant='ghost'
            >
              <Icon name='helpicon' size='smaller' color='nepal' />
            </LBButton>
          </Flex>
        ) : null}
      </Flex>
    ) : null}
    {Object.keys(options).map((key, idx) => (
      <StyledFormControlLabel
        key={idx}
        control={
          <Checkbox
            onChange={() =>
              onChange(key, !options[key].isChecked, options[key])
            }
            checked={options[key].isChecked}
            value={options[key].isChecked}
            disabled={options[key].disabled}
          />
        }
        label={userReadableLabel(key)}
      />
    ))}
  </FormControlWrapper>
);

const optionsFormatedForDropdown = (
  options,
  addAvatars,
  getAvatar = () => null
) =>
  options.map((option = {}) => {
    const { id, name } = option;
    const avatar = getAvatar(option);

    return {
      value: id,
      displayValue: (
        <Flex items='center'>
          {addAvatars ? (
            <Padding l='tiny' r='small'>
              <MultipleAvatars avatars={[avatar]} />
            </Padding>
          ) : null}
          <Text size='medium' ellipsisAfter>
            {name}
          </Text>
        </Flex>
      ),
      searchableValue: name
    };
  });

export const WrappedAutocomplete = ({
  title,
  name,
  placeholderName,
  options = [],
  onChange,
  selectedOptions,
  addAvatars,
  getAvatar,
  autoCompleteProps = {},
  disableClearable = false,
  multiple = true
}) => {
  const onChangeFn = name => items => {
    onChange(name, items);
  };

  const value = multiple ? selectedOptions : selectedOptions[0] || null;
  return (
    <Autocomplete
      width='100%'
      multiple={multiple}
      selectedOptionValue={value}
      disableClearable={disableClearable}
      options={optionsFormatedForDropdown(options, addAvatars, getAvatar)}
      onChange={onChangeFn(name)}
      placeholder={`All ${title || name}`}
      tooltipTitle={`${placeholderName || title || name} selected`}
      {...autoCompleteProps}
    />
  );
};

export const trackSearch = (
  eventTag,
  search = {},
  sensitiveKeys = [],
  page,
  page_tab
) => {
  const nonSensitiveSearchParams = _.omit(search, sensitiveKeys);
  const sensitiveSearchParamsWithoutValues = sensitiveKeys.reduce(
    (acc, key) => {
      if (!Object.keys(search).includes(key)) return acc;

      return {
        ...acc,
        [key]: 'N/A'
      };
    },
    {}
  );

  const trackableSearchParams = {
    ...nonSensitiveSearchParams,
    ...sensitiveSearchParamsWithoutValues
  };

  TrackingService.logEvent(eventTag, {
    page,
    ...(page_tab ? { page_tab } : {}),
    search: trackableSearchParams
  });
};

const transfromCompareFilterToServerFilter = (value = '') => {
  switch (true) {
    case value.includes('<'):
      return { lt: Number(value.replace(/\D/g, '')) };
    case value.includes('>'):
      return { gt: Number(value.replace(/\D/g, '')) };
    case value.includes('-'):
      return {
        between: value.split('-').map(v => Number(v.replace(/\D/g, '')))
      };
    default:
      return {};
  }
};

export const convertSearchToRequest = (search, filtersConfig) => {
  const searchString = QueryString.stringify(search);
  const reqFilterBody = Object.keys(search).reduce((acc, key) => {
    const {
      reqKey = '',
      type,
      searchValueTransformFn = null
    } = _.get(filtersConfig, key, {});

    switch (type) {
      case 'order_by':
        const [field, order] = search[key].split(',');
        return {
          ...acc,
          order_by: {
            [field]: order
          }
        };
      case 'pit': {
        const value = extractFromQueryString('pit', '', searchString);

        return {
          ...acc,
          point_in_time: searchValueTransformFn
            ? searchValueTransformFn(value)
            : moment(value).format(SEARCH_DATE_FORMAT)
        };
      }

      case 'multi-autocomplete':
      case 'multi-tree-dropdown':
      case 'multi-checkbox': {
        const value = extractFromQueryString(key, '', searchString);
        const parsedValue = _.isFunction(searchValueTransformFn)
          ? searchValueTransformFn(value)
          : value.split(',').map(val => {
              const isNum = /^\d+$/.test(val);
              return isNum ? _.toNumber(val) : val;
            });
        return {
          ...acc,
          ...(reqKey
            ? { [reqKey]: parsedValue }
            : _.isObject(parsedValue)
            ? { ...parsedValue }
            : {})
        };
      }
      case 'date':
        const dateBeforeKey = extractFromQueryString(
          'date_before_key',
          'before',
          searchString
        );
        const dateFormat = extractFromQueryString(
          'date_format',
          SEARCH_DATE_FORMAT,
          searchString
        );
        const [from, to] = extractFromQueryString(key, '', searchString).split(
          ','
        );

        if (!to && !from) return acc;

        return {
          ...acc,
          [reqKey]: _.isFunction(searchValueTransformFn)
            ? searchValueTransformFn(
                extractFromQueryString(key, '', searchString)
              )
            : {
                ...(to
                  ? {
                      [dateBeforeKey]: moment(to).format(dateFormat)
                    }
                  : {}),
                ...(from
                  ? { after: moment(from).format(SEARCH_DATE_FORMAT) }
                  : {})
              }
        };
      case 'radio': {
        const value = extractFromQueryString(key, null, searchString);

        return {
          ...acc,
          ...(value
            ? {
                [reqKey]: _.isFunction(searchValueTransformFn)
                  ? searchValueTransformFn(value)
                  : transfromCompareFilterToServerFilter(value || '')
              }
            : {})
        };
      }
      case 'query_only': {
        const value = extractFromQueryString(key, null, searchString);
        return {
          ...acc,
          [reqKey]: _.isFunction(searchValueTransformFn)
            ? searchValueTransformFn(value)
            : value
        };
      }
      default:
        return acc;
    }
  }, {});
  return {
    ...reqFilterBody,
    ...(reqFilterBody.point_in_time ||
    !Object.values(filtersConfig).find(({ type }) => type === 'pit')
      ? {}
      : { point_in_time: moment().format(SEARCH_DATE_FORMAT) })
  };
};

/**
 * Return the changes that need to be made to the url search query.
 *  i.e: if the url looks like ?created=today-20,today-10 it should be transformed into ?created=2020-03-10,2020-03-20
 * @param {Record<string, any>} search
 * @param {Record<string, any>} filtersConfig
 * @returns {object}
 */
export const updateInitialSearch = (search, filtersConfig) => {
  const searchString = QueryString.stringify(search);
  return Object.keys(filtersConfig).reduce((acc, key) => {
    const { type } = _.get(filtersConfig, key, {});

    switch (type) {
      case 'date': {
        const value = extractFromQueryString(key, '', searchString);
        const relativeExpressionsRegEx = /(<|>)(@today)((-|\+)([0-9]*))?/;

        let updatedDateString = `${value}`;
        let from = '';
        let to = '';

        if (value.match(relativeExpressionsRegEx)) {
          const parts = value.includes('-')
            ? value.split('-')
            : value.split('+');
          const relativeMoment = moment();
          const momentDeltaFunction = value.includes('-') ? 'subtract' : 'add';
          const numberOfDaysDelta = parts[1];
          const updatedMoment = relativeMoment
            .clone()
            [momentDeltaFunction](numberOfDaysDelta, DAYS_PARAM);
          const formattedUpdatedMoment =
            updatedMoment.format(SEARCH_DATE_FORMAT);

          if (value.includes('<')) {
            to = formattedUpdatedMoment;
          } else {
            from = formattedUpdatedMoment;
          }

          updatedDateString = [from, to].join(',');
        }

        if (updatedDateString !== value) {
          return {
            ...acc,
            [key]: updatedDateString
          };
        }

        return acc;
      }
      default:
        return acc;
    }
  }, {});
};

export const setQueryFilter = query => {
  const currQuery = QueryString.parse(history.location.search, {
    ignoreQueryPrefix: true
  });
  const newQuery = { ...currQuery, ...query };
  const newQueryString = `?${QueryString.stringify(newQuery, {
    arrayFormat: 'comma'
  })}`;
  history.push({
    pathname: history.location.pathname,
    search: newQueryString
  });
};

export const AUTOCOMPLETE_TYPES = {
  AUTOCOMPLETE: 'autocomplete',
  TREE_TEAMS_DROPDOWN: 'treeTeamsDropdown'
};

export const handleSearchParams = ({ filtersConfig }) => {
  // eslint-disable-next-line no-restricted-globals
  const search = QueryString.parse(location.search, {
    ignoreQueryPrefix: true
  });
  let searchParams = search;

  Object.keys(search).forEach(key => {
    const { removeFromSearchParamsFn, options } = filtersConfig[key] || {};
    const values = search[key].split(',');

    if (removeFromSearchParamsFn) {
      const formattedSearchParams =
        removeFromSearchParamsFn({
          values,
          options: options.map(option => option?.id?.toString()).filter(Boolean)
        }) || [];

      if (formattedSearchParams.length) {
        searchParams = Object.assign(search, {
          [key]: formattedSearchParams.join(',')
        });
      } else {
        searchParams = _.omit(search, [key]);
      }
    }
  });

  return searchParams;
};

/**
 * Format datetime to display in chip.
 * @example
 * formatDatetimeForChips('2023-06-28 14:52:41') // '2023-06-28 14:52'
 * @example
 * formatDatetimeForChips('2023-06-28') // '2023-06-28'
 * @param {string} date
 * @returns {string}
 */
export const formatDatetimeForChips = date => {
  if (moment(date, SEARCH_TIMEDATE_FORMAT, true).isValid()) {
    return moment(date).format(SEARCH_TIMEDATE_FORMAT_DISPLAY);
  }

  if (moment(date, SEARCH_DATE_FORMAT, true).isValid()) {
    return moment(date).format(SEARCH_DATE_FORMAT);
  }

  return date;
};
